// Vendor imports
const classNames = require('classnames')
// Shared imports
const BemEntity = require('shared/bem')
const { TYPES: modelTypes } = require('shared/model-types')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const Icon = require('client-v2/common/components/icon')
const { getDisplayName } = require('client-v2/common/components/utils')
const { COLLECTION_FOLLOWING_CHANGE_PENDING } = require('client-v2/common/models/collection/actions')
const { INTEREST_FOLLOWING_CHANGE_PENDING } = require('client-v2/common/models/interest/actions')
const { USER_FOLLOWING_CHANGE_PENDING } = require('client-v2/common/models/user/actions')

const bem = BemEntity('ModelToggleButton')

function ModelToggleButton(props) {
  const {
    pending, checked, unchecked,
    iconId, label, title, action
  } = modelTypeProps(props)
  return (
    tag(props.as, {
      className: classNames(
        props.className,
        bem.block({
          action,
          pending,
          checked,
          unchecked
        })
      ),
      title,
      forwardedRef: props.forwardedRef,
      onClick: pending ? null : props.onClick
    },
      !!iconId && tag(Icon, {
        className: bem.element('icon'),
        id: iconId
      }),
      tag('label', {
        className: bem.element('label')
      },
        label
      )
    )
  )
}

const propsByModelType = {
  [modelTypes.INTEREST]: {
    action: 'interest-follow',
    uncheckedIconId: 'plus',
    uncheckedLabel: 'Follow',
    uncheckedTitle: 'Follow this interest to see more like this',
    checkedIconId: 'check',
    checkedLabel: 'Following',
    checkedTitle: 'You are following this interest',
    pendingValue: INTEREST_FOLLOWING_CHANGE_PENDING
  },
  [modelTypes.MIX]: {
    action: 'subscribe',
    uncheckedIconId: 'collection-subscribe',
    uncheckedLabel: 'Subscribe',
    uncheckedTitle: 'Subscribe to this collection',
    checkedIconId: 'collection-subscribed',
    checkedLabel: 'Subscribed',
    checkedTitle: 'You are subscribed to this collection',
    pendingValue: COLLECTION_FOLLOWING_CHANGE_PENDING
  },
  [modelTypes.USER]: {
    action: 'user-follow',
    uncheckedIconId: 'user-follow',
    uncheckedLabel: 'Follow',
    uncheckedTitle: 'Follow this person to see more of their posts',
    checkedIconId: 'user-followed',
    checkedLabel: 'Following',
    checkedTitle: 'You are following this person',
    pendingValue: USER_FOLLOWING_CHANGE_PENDING
  }
}

function modelTypeProps({ modelType, checked }) {
  const modelTypeProps = propsByModelType[modelType]
  if (!modelTypeProps) {
    throw new Error(`Unsupported modelType: ${modelType}`)
  }
  if (checked === modelTypeProps.pendingValue) {
    return {
      pending: true,
      label: '',
      title: 'Just a moment...'
    }
  }
  if (checked) {
    return {
      checked: true,
      iconId: modelTypeProps.checkedIconId,
      label: modelTypeProps.checkedLabel,
      title: modelTypeProps.checkedTitle,
      action: modelTypeProps.action
    }
  }
  return {
    unchecked: true,
    iconId: modelTypeProps.uncheckedIconId,
    label: modelTypeProps.uncheckedLabel,
    title: modelTypeProps.uncheckedTitle,
    action: modelTypeProps.action
  }
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')
  const morePropTypes = require('client-v2/common/utils/prop-types')

  const checkedValues = [
    true,
    false,
    COLLECTION_FOLLOWING_CHANGE_PENDING,
    INTEREST_FOLLOWING_CHANGE_PENDING,
    USER_FOLLOWING_CHANGE_PENDING
  ]

  const modelTypeValues = Object.keys(propsByModelType)

  ModelToggleButton.propTypes = {
    // Refs
    forwardedRef: morePropTypes.domRef,
    // Values
    as: PropTypes.elementType.isRequired,
    className: PropTypes.string,
    checked: PropTypes.oneOf(checkedValues).isRequired,
    modelType: PropTypes.oneOf(modelTypeValues).isRequired,
    // Callbacks
    onClick: PropTypes.func.isRequired
  }
}

 function withModelToggle(modelType, buttonComponent) {
  function WithModelToggle(props) {
    return (
      tag(ModelToggleButton, {
        ...props,
        as: buttonComponent,
        modelType
      })
    )
  }
  const buttonName = getDisplayName(buttonComponent)
  WithModelToggle.displayName = `withModelToggle(${buttonName}, ${modelType})`
  return WithModelToggle
}

module.exports = Object.assign(ModelToggleButton, {
  withModelToggle
})
